import angular from 'angular';
import templateKformBuilderOptionCard from './card/kform-builder-option-card.html';

/**
 * This directive creates a field for tweaking component options.
 * This needs at least a property attribute specifying what property
 * of the component to bind to.
 *
 * If the property is defined in COMMON_OPTIONS above, it will automatically
 * populate its label, placeholder, input type, and tooltip. If not, you may specify
 * those via attributes (except for tooltip, which you can specify with the title attribute).
 * The generated input will also carry over any other properties you specify on this directive.
 *
 * @param {any} COMMON_OPTIONS
 * @param {import('angular').IFilterService} $filter
 * @returns {*} Directive
 */
const kformBuilderOptionDirective = /* @ngInject */ (COMMON_OPTIONS, $filter) => ({
	restrict: 'E',
	require: 'property',
	priority: 2,
	replace: true,
	/**
	 * @param {any} element
	 * @param {any} attributes
	 * @returns {{}}
	 */
	template(element, attributes) {
		const translate = $filter('translate');

		const { property, $attr } = attributes;
		const label = attributes.label || (COMMON_OPTIONS[property]?.label);
		const placeholder = attributes.placeholder
			|| (COMMON_OPTIONS[property]?.placeholder)
			|| null;
		const disabled = (!!attributes.disabled && attributes.disabled !== 'false')
			|| (COMMON_OPTIONS[property]?.disabled)
			|| false;
		const type = attributes.type || (COMMON_OPTIONS[property]?.type) || 'text';
		let tooltip = attributes.tooltip || (COMMON_OPTIONS[property]?.tooltip) || '';
		const step = attributes.step || (COMMON_OPTIONS[property]?.step) || '';

		const input = type === 'textarea' ? angular.element('<textarea></textarea>') : angular.element('<input>');

		/** @type {any} */
		const inputAttributes = {
			id: property,
			name: property,
			disabled,
			type,
			'ng-model': `component.${ property}`,
			placeholder: translate(placeholder),
		};

		if (step) {
			inputAttributes.step = step;
		}

		// Pass through attributes from the directive to the input element
		for (const key of Object.values($attr)) {
			inputAttributes[key] = attributes[attributes.$normalize(key)];
			// Allow specifying tooltip via title attr
			if (key.toLowerCase() === 'title') {
				tooltip = attributes[key];
			}
		}

		if (property === 'label') {
			inputAttributes['label-validator'] = '';
			inputAttributes.required = '';
			inputAttributes.autofocus = true;
		}
		// Add min/max value floor values for validation.
		if (property === 'validate.minLength' || property === 'validate.maxLength') {
			inputAttributes.min = 0;
		}

		input.attr(inputAttributes);

		// Checkboxes have a slightly different layout
		if (inputAttributes.type && inputAttributes.type.toLowerCase() === 'checkbox') {
			let html = `<div class="form-group form-group-sm">
	<label class="control-label" kform-builder-tooltip="${translate(tooltip)}">${translate(label)}</label>
		<div class="input-group">
`;
			for (const [optionLabel, value] of Object.entries({ Yes: true, No: false, Auto: null })) {
				html += '<label class="radio-inline">';
				input.attr('type', 'radio');
				input.attr('ng-value', angular.toJson(value));
				input.attr('id', `${property }-${ angular.toJson(value)}`);
				html += input.prop('outerHTML');
				html += ` ${ translate(optionLabel)}`;
				html += '</label>';
			}
			html += '</div></div>';

			return html;
		}
		else if (inputAttributes.type === 'card') {
			return templateKformBuilderOptionCard;
		}

		let helpMessage = '';
		switch (property) {
		case 'label': {
			helpMessage = '<span class="help-block" ng-if="!component.label">You must provide a label for this component.'
				+ '</span>';
			break;
		}
		default: {
			break;
		}
		}

		input.addClass('form-control');

		// eslint-disable-next-line prefer-template
		return '<div class="form-group form-group-sm">'
			+ '<label class="control-label" for="' + property + '" kform-builder-tooltip="' + translate(tooltip) + '">'
			+ translate(label) + '</label><div class="input-group">'
			+ input.prop('outerHTML')
			+ helpMessage
			+ '</div></div>';
	},
});

export default kformBuilderOptionDirective;
